<template>
  <section class="settings-page">

    <!-- Header -->
    <div class="settings-header">
      <h1>Settings</h1>
      <p>Control your identity, audience ownership, and platform access.</p>
    </div>

    <div class="settings-layout">

      <!-- ================= LEFT TABS ================= -->
      <aside class="settings-tabs" style="height: 15rem;">
        <button v-for="tab in tabs" :key="tab.key" @click="activeTab = tab.key"
          :class="['tab-btn', activeTab === tab.key ? 'active' : '']">
          {{ tab.label }}
        </button>
      </aside>

      <!-- ================= CONTENT ================= -->
      <div class="settings-content">

        <!-- ACCOUNT -->
        <section v-if="activeTab === 'account'" class="card">
          <h3>Account</h3>

          <div class="field">
            <label>Display Name</label>
            <input v-model="account.name" />
          </div>

          <div class="field inline">
            <span>Email</span>
            <span class="status verified">{{ account.email }}</span>
          </div>

          <div class="field inline">
            <span>Phone</span>
            <span class="status verified">{{ account.phone }}</span>
          </div>

          <div class="action-row">

          <button class="primary-btn" @click="updateName">
            {{isLoading ? "Updating..." : "Update"}}
          </button>


          <button @click="deacNow()" class="primary-btn danger-primary">
            <i class='bx bx-trash'></i>
            Deactivate Account
          </button>

        </div>

        </section>

        <!-- ACCOUNT RECOVERY -->
        <section v-if="activeTab === 'recovery'" class="card">
          <h3>Account Recovery</h3>

          <p class="section-desc">
            Recover your audience if your account is lost, hacked, or migrated.
          </p>

          <!-- Creator Name -->
          <div class="field">
            <label>Creator Name</label>
            <input v-model="creatorName" />
          </div>

          <!-- Old Username -->
          <div class="field" style="margin-top: 1rem;">
            <label>Old Username / Handle</label>
            <input v-model="oldHandle" />
          </div>

          <!-- Platform -->
          <div class="field" style="margin-top: 1rem;">
            <label>Platform</label>
            <input v-model="platform" placeholder="Instagram / YouTube etc" />
          </div>

          <!-- Reason -->
          <div class="field" style="margin-top: 1rem;">
            <label>Reason</label>
            <select v-model="reason">
              <option disabled value="">Select reason</option>
                <option value="Account hacked">Account hacked</option>
                <option value="Account banned / suspended">Account banned / suspended</option>
                <option value="Lost login access">Lost login access</option>
                <option value="Account deleted">Account deleted</option>
                <option value="Moving to new account">Moving to new account</option>
                <option value="Other">Other</option>
            </select>
          </div>

          <!-- New Platform -->
          <div class="field" style="margin-top: 1rem;">
            <label>New Platform</label>
            <!-- <input v-model="newPlatform" /> -->
              <select v-model="newPlatform">
                <option value="">Select Platform</option>
                <option value="Instagram">Instagram</option>
                <option value="YouTube">YouTube</option>
                <option value="TikTok">TikTok</option>
                <option value="Facebook">Facebook</option>
                <option value="Reddit">Reddit</option>
                <option value="X">X (Twitter)</option>
                <option value="Other">Other</option>
            </select>
          </div>

          <!-- New Handle -->
          <div class="field" style="margin-top: 1rem;">
            <label>New Username / Handle</label>
            <input v-model="newHandle" />
          </div>

          <!-- New Profile Link -->
          <div class="field" style="margin-top: 1rem;">
            <label>New Profile Link</label>
            <input v-model="newLink" />
          </div>

          <!-- Message -->
          <div class="field" style="margin-top: 1rem;">
            <label>Message to Fans (Max 300 characters)</label>
            <textarea v-model="message" maxlength="300"></textarea>
            <small>{{ message.length }}/300</small>
          </div>

          <!-- Proof -->
          <div class="field" style="margin-top: 1rem;">
            <label>Verification Proof (Drive / Screenshot link)</label>
            <input v-model="proof" />
          </div>

          <!-- Old Account Link -->
          <div class="field" style="margin-top: 1rem;">
            <label>Old Account Link</label>
            <input v-model="oldLink" />
          </div>

          <div class="terms-box">
            <input type="checkbox" v-model="acceptTerms" />
            <span style="margin-top: .3rem;">
              I confirm that I am the rightful owner of this account and the information provided is accurate. 
              Misuse may lead to account suspension.
            </span>
          </div>
          <div class="action-row">
            <button class="primary-btn" @click="submitRecovery">
              {{ loading ? "Submitting..." : "Submit Recovery Request" }}
            </button>
          </div>
          
        </section>

        <section v-if="activeTab === 'chpass'" class="card security-card">
          <h3>
            <i class='bx bx-lock'></i>
            Change Password
          </h3>

          <div class="field">
            <label>Current Password</label>
            <input type="password" v-model="passwordForm.current" placeholder="Enter current password" />
          </div>

          <div class="field">
            <label>New Password</label>
            <input type="password" v-model="passwordForm.new" placeholder="Minimum 6 characters" />
          </div>

          <div class="field">
            <label>Confirm New Password</label>
            <input type="password" v-model="passwordForm.confirm" placeholder="Re-enter new password" />
          </div>

          <p v-if="passwordForm.error" class="form-error">
            {{ passwordForm.error }}
          </p>

          <p v-if="passwordForm.success" class="form-success">
            Password updated successfully
          </p>

          <div class="action-row">
            <button class="primary-btn" @click="updatePassword" :disabled="passwordForm.loading">
              <i class='bx bx-key'></i>

              {{ passwordForm.loading ? "Updating..." : "Update Password" }}
            </button>

          </div>
        </section>



        <!-- AUDIENCE & PRIVACY -->
        <section v-if="activeTab === 'audience'" class="card">
          <h3>Audience & Privacy</h3>

          <div class="toggle">
            <span>Allow brand discovery</span>
            <input type="checkbox" v-model="audience.brandDiscovery" />
          </div>

          <div class="toggle">
            <span>Share aggregated interests</span>
            <input type="checkbox" v-model="audience.shareInterests" />
          </div>

          <div class="toggle">
            <span>Retain fan graph if plan expires</span>
            <input type="checkbox" v-model="audience.retainGraph" />
          </div>
        </section>

        <!-- VERIFICATION -->
        <section v-if="activeTab === 'verification'" class="card">
          <h3>Verification & Trust</h3>

          <div class="info-row">
            <span>Email Verification</span>
            <span class="status verified" v-if="user_dt!=null && user_dt.user_email_verified==='yes'">Verified</span>
            <span class="status notverified" v-if="user_dt!=null && user_dt.user_email_verified!='yes'">Not Verified</span>
          </div>

          <div class="info-row">
            <span>Phone Verification</span>
            <span class="status verified" v-if="user_dt!=null && user_dt.user_mobile_verified==='yes'">Verified</span>
            <span class="status notverified" v-if="user_dt!=null && user_dt.user_mobile_verified!='yes'">Not Verified</span>
          </div>

          <div class="info-box">
            Trust Score: <strong>{{ trustScore }}%</strong>
          </div>

          <!-- <div class="toggle">
            <span>Require email + phone for fans</span>
            <input type="checkbox" />
          </div> -->
        </section>



        <!-- BRAND ACCESS -->
        <section v-if="activeTab === 'brands'" class="card">
          <h3>Brand Access</h3>

          <div class="toggle">
            <span>Visible to brands</span>
            <input type="checkbox" v-model="brands.visible" />
          </div>

          <div class="toggle">
            <span>Show engagement score</span>
            <input type="checkbox" v-model="brands.showEngagement" />
          </div>

          <button class="ghost-btn">Preview Brand Profile</button>
        </section>


        <!-- INTEGRATIONS -->
        <section v-if="activeTab === 'integrations'" class="card">
          <h3>Integrations</h3>

          <p class="section-desc">
            Connect trusted services to verify identity, protect your audience,
            and automate workflows. No fan data is ever shared individually.
          </p>

          <!-- ================= CORE ================= -->
          <div class="integration-group">
            <h4>Core</h4>

            <div class="integration-item" v-for="item in coreIntegrations" :key="item.key">
              <div class="left">
                <i class="bx" :class="item.icon"></i>
                <div>
                  <strong>{{ item.name }}</strong>
                  <p>{{ item.desc }}</p>
                </div>
              </div>

              <button class="connect-btn" :class="item.connected ? 'connected' : ''">
                {{ item.connected ? 'Connected' : 'Connect' }}
              </button>
            </div>
          </div>

          <!-- ================= PAYMENTS ================= -->
          <div class="integration-group">
            <h4>Payments</h4>

            <div class="integration-item" v-for="item in paymentIntegrations" :key="item.key">
              <div class="left">
                <i class="bx" :class="item.icon"></i>
                <div>
                  <strong>{{ item.name }}</strong>
                  <p>{{ item.desc }}</p>
                </div>
              </div>

              <button class="connect-btn connected">Connected</button>
            </div>
          </div>

          <!-- ================= SOCIAL VERIFICATION ================= -->
          <div class="integration-group">
            <h4>Social Verification</h4>

            <div class="integration-item" v-for="item in socialIntegrations" :key="item.key">
              <div class="left">
                <i class="bx" :class="item.icon"></i>
                <div>
                  <strong>{{ item.name }}</strong>
                  <p>{{ item.desc }}</p>
                </div>
              </div>

              <button class="connect-btn" :class="item.connected ? 'connected' : ''">
                {{ item.connected ? 'Connected' : 'Verify' }}
              </button>
            </div>
          </div>

          <!-- ================= AUTOMATION ================= -->
          <div class="integration-group">
            <h4>Automation & API</h4>

            <div class="integration-item disabled">
              <div class="left">
                <i class='bx bx-cog'></i>
                <div>
                  <strong>Zapier / Webhooks</strong>
                  <p>Trigger events like new fan verification or milestones</p>
                </div>
              </div>
              <span class="badge">Coming Soon</span>
            </div>

            <div class="integration-item disabled">
              <div class="left">
                <i class='bx bx-key'></i>
                <div>
                  <strong>API Access</strong>
                  <p>Read-only aggregated audience metrics</p>
                </div>
              </div>
              <span class="badge">Pro+</span>
            </div>
          </div>
        </section>


      </div>
    </div>
    <!-- DEACTIVATE CONFIRMATION -->
    <div v-if="showDeactivateDialog" class="modal-overlay">
      <div class="modal-card">

        <h3 class="text-xl font-bold">
          Deactivate Account
        </h3>

        <p class="text-sm text-slate-400 mt-2">
          This action will disable your brand account access.
          You can reactivate later via support.
        </p>

        <div class="flex justify-end gap-4 mt-8">
          <button @click="showDeactivateDialog=false" class="dialog-btn muted">
            Cancel
          </button>

          <button @click="deactivateAccount" class="dialog-btn danger">
            Yes, Deactivate
          </button>
        </div>

      </div>
    </div>

    <!-- UPDATE NAME MODAL -->
    <div v-if="showNameModal" class="modal-overlay">

      <div class="modal-card">

        <h3>Update Name</h3>

        <div class="field">
          <label>Full Name</label>
          <input v-model="editName" type="text" placeholder="Enter your full name" />
        </div>

        <div class="field">
          <label>Phone</label>
          <input v-model="editMob" type="text" placeholder="Enter your phone" />
        </div>

        <div class="modal-actions">

          <button class="ghost-btn" @click="showNameModal=false">
            Cancel
          </button>

          <button class="primary-btn" @click="updateName">
            {{isLoading ? "Saving..." : "Save"}}
          </button>

        </div>

      </div>

    </div>
  </section>
</template>

<script>
  import { useToast } from "vue-toastification";
  import { changePasswordApi, deactivateAccountApi,chkPlanApi,submitRecoveryApi } from "../apis/authapis";
  
  import { updateFanProfile } from "../apis/fanapis";

  export default {
    name: "SettingsScreen",

    data() {
      return {
        acceptTerms: false,
        creatorName: "",
        oldHandle: "",
        platform: "",
        reason: "",
          newPlatform: "",
          newHandle: "",
          newLink: "",
          message: "",
          proof: "",
          oldLink: "",
          loading: false,
        toast: null,
        ldata: null,
        isLoading:false,
        showNameModal: false,
        user_dt:null,
        editName: "",
        editMob: "",
        showDeactivateDialog: false,
        passwordForm: {
          current: "",
          new: "",
          confirm: "",
          loading: false,
          error: "",
          success: false
        }
        ,
        activeTab: "account",
        coreIntegrations: [
          {
            key: "email",
            name: "Email (SMTP)",
            desc: "Email verification and system notifications",
            icon: "bx-envelope",
            connected: true
          },
          {
            key: "sms",
            name: "SMS / WhatsApp",
            desc: "Phone OTP via Twilio or MSG91",
            icon: "bx-message-square-dots",
            connected: true
          },
          {
            key: "captcha",
            name: "Bot Protection",
            desc: "Prevent fake fans and automated signups",
            icon: "bx-shield-quarter",
            connected: true
          }
        ],
        paymentIntegrations: [
          {
            key: "stripe",
            name: "Stripe",
            desc: "Subscriptions, invoices, and renewals",
            icon: "bx-credit-card"
          },
          {
            key: "razorpay",
            name: "Razorpay",
            desc: "India-focused payments and subscriptions",
            icon: "bx-bank"
          }
        ],

        socialIntegrations: [
          {
            key: "instagram",
            name: "Instagram",
            desc: "Verify creator identity (read-only)",
            icon: "bxl-instagram",
            connected: false
          },
          {
            key: "youtube",
            name: "YouTube",
            desc: "Channel ownership verification",
            icon: "bxl-youtube",
            connected: true
          },
          {
            key: "twitter",
            name: "X (Twitter)",
            desc: "Public profile verification",
            icon: "bxl-twitter",
            connected: false
          },
          {
            key: "linkedin",
            name: "LinkedIn",
            desc: "Professional identity verification",
            icon: "bxl-linkedin",
            connected: false
          }
        ]
        ,
        tabs: [
          { key: "account", label: "Account" },
          { key: "chpass", label: "Change Password" },
          { key: "verification", label: "Verification & Trust" },
          { key: "recovery", label: "Account Recovery" }
        ],

        account: {
          name: "***** *****",
          email: "**********",
          phone: "+91 XXXXXXXX"
        },

        audience: {
          brandDiscovery: true,
          shareInterests: true,
          retainGraph: true
        },

        brands: {
          visible: true,
          showEngagement: true
        },

        plan: {
          name: "PRO",
          renewal: "Jan 12, 2025"
        },

        trustScore: 0,

        notifications: [
          { key: "fan", label: "New fan verified", enabled: true },
          { key: "milestone", label: "Audience milestones", enabled: true },
          { key: "brand", label: "Brand profile views", enabled: true },
          { key: "payment", label: "Payment reminders", enabled: true },
          { key: "security", label: "Security alerts", enabled: true }
        ]
      };
    },
    mounted() {
      this.toast = useToast();
      const ldataRaw = sessionStorage.getItem("ldata");
      this.ldata = ldataRaw ? JSON.parse(ldataRaw) : null;
      this.account.name = this.ldata.user_name;
      this.account.email = this.ldata.user_email;
      this.account.phone = this.ldata.user_mobile;
      this.creatorName = this.ldata.user_name;
      this.initUser();
    }
    ,
    methods: {
     async submitRecovery() {


        // ✅ VALIDATION (use recovery object)
        if (!this.creatorName ||
            !this.oldHandle ||
            !this.platform ||
            !this.reason ||
            !this.newHandle ||
            !this.newLink) {

          this.toast.error("Please fill all required fields");
          return;
        }

        if (this.message && this.message.length > 300) {
          this.toast.error("Message exceeds 300 characters");
          return;
        }

        // if (!this.acceptTerms) {
        //   this.toast.error("Please accept terms & conditions");
        //   return;
        // }

        this.loading = true;

        try {

          // ✅ CLEAN PAYLOAD (important for backend)
          const payload = {
            user_id: this.ldata.user_id,
            creator_name: this.creatorName,
            old_handle: this.oldHandle,
            platform: this.platform,
            reason: this.reason,
            new_platform: this.newPlatform,
            new_handle: this.newHandle,
            new_profile_link: this.newLink,
            message: this.message,
            proof_link: this.proof,
            old_account_link: this.oldLink
          };

          await submitRecoveryApi(payload);

          this.toast.success("Recovery request submitted");

          // ✅ RESET
           this.creatorName = "";
            this.oldHandle = "";
            this.platform = "";
            this.reason = "";
            this.newPlatform = "";
            this.newHandle = "";
            this.newLink = "";
            this.message = "";
            this.proof = "";
            this.oldLink = "";


        } catch (e) {
          this.toast.error("Failed to submit");
        }

        this.loading = false;
      },
      async initUser(){
        const res = await chkPlanApi(this.ldata.user_id);

        if (res.status) {

          this.user_dt = res.user_dt1;
          this.trustScore = res.trustscore;
        }
      },
      openNameModal() {

        this.editName = this.ldata.user_name || "";
        this.editMob = this.ldata.user_mobile || "";
        this.showNameModal = true;

      },
      async updateName() {

        if (!this.account.name.trim()) {
          this.toast.error("Name cannot be empty");
          return;
        }


        this.isLoading = true;

        const res = await updateFanProfile({
          user_id: this.ldata.user_id,
          user_name: this.account.name,
          user_mobile: this.account.phone
        });

        this.isLoading = false;

        if (res.status) {

          this.ldata.user_name = this.account.name

          sessionStorage.setItem(
            "ldata",
            JSON.stringify(this.ldata)
          );

          this.toast.success("Name updated");

          this.showNameModal = false;

        } else {

          this.toast.error(res.success || "Failed");

        }

      },
      deacNow() {
        this.showDeactivateDialog = true;
      },
      async deactivateAccount() {
        const ldataRaw = sessionStorage.getItem("ldata");
        const ldata = ldataRaw ? JSON.parse(ldataRaw) : null;
        const res = await deactivateAccountApi(ldata.user_id);


        if (res.status) {
          this.toast.success("Account deactivated");
          this.showDeactivateDialog = false;

          sessionStorage.removeItem("ldata");
          window.location.href = "/login";

        } else {

          this.toast.error(res.success || "Deactivation failed");
        }
      },
      async updatePassword() {

        this.passwordForm.error = "";
        this.passwordForm.success = false;

        if (!this.passwordForm.current ||
          !this.passwordForm.new ||
          !this.passwordForm.confirm) {

          this.passwordForm.error = "All fields required";
          return;
        }

        if (this.passwordForm.new.length < 6) {
          this.passwordForm.error = "Password must be at least 8 characters";
          return;
        }

        if (this.passwordForm.new !== this.passwordForm.confirm) {
          this.passwordForm.error = "Passwords do not match";
          return;
        }

        this.passwordForm.loading = true;

        const ldataRaw = sessionStorage.getItem("ldata");
        const ldata = ldataRaw ? JSON.parse(ldataRaw) : null;

        const res = await changePasswordApi({
          user_id: ldata.user_id,
          current_password: this.passwordForm.current,
          new_password: this.passwordForm.new

        });

        this.passwordForm.loading = false;

        if (!res.status) {
          this.passwordForm.error = res.success || "Password update failed";
          return;
        }

        this.passwordForm.success = true;

        // reset fields
        this.passwordForm.current = "";
        this.passwordForm.new = "";
        this.passwordForm.confirm = "";
      }
    }

  };
</script>

<style scoped>
  .settings-page {
    max-width: 1440px;
    margin: auto;
    padding: 48px 48px 120px;
  }

  .settings-header h1 {
    font-size: 40px;
    font-weight: 800;
  }

  .settings-header p {
    color: #94a3b8;
    margin-top: 6px;
  }

  .settings-layout {
    display: grid;
    grid-template-columns: 260px 1fr;
    gap: 48px;
    margin-top: 48px;
  }

  /* Tabs */
  .settings-tabs {
    background: #fff;
    border-radius: 24px;
    padding: 12px;
    box-shadow: 0 8px 40px -12px rgba(0, 0, 0, 0.08);
  }

  .tab-btn {
    width: 100%;
    text-align: left;
    padding: 14px 16px;
    border-radius: 14px;
    font-weight: 600;
    color: #475569;
  }

  .tab-btn:hover {
    background: #f8fafc;
  }

  .tab-btn.active {
    background: #0f172a;
    color: white;
  }

  /* Cards */
  .card {
    background: white;
    border-radius: 32px;
    padding: 40px;
    box-shadow: 0 8px 40px -12px rgba(0, 0, 0, 0.08);
  }

  .card h3 {
    font-size: 24px;
    font-weight: 800;
    margin-bottom: 32px;
  }

  /* Fields */
  .field label {
    font-size: 11px;
    text-transform: uppercase;
    color: #94a3b8;
    font-weight: 700;
  }

  .field input {
    width: 100%;
    margin-top: 6px;
    padding: 14px;
    border-radius: 14px;
    background: #f8fafc;
  }

  /* Inline rows */
  .field.inline,
  .info-row,
  .toggle {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 0;
    border-bottom: 1px solid #f1f5f9;
  }

  /* Status */
  .status {
    font-size: 11px;
    padding: 4px 10px;
    border-radius: 999px;
  }

  .status.verified {
    background: #ecfdf5;
    color: #047857;
  }
   .status.notverified {
    background: #fee2e2;
    color: #b91c1c;
  }

  /* Buttons */
  .primary-btn {
    background: #0f172a;
    color: white;
    padding: 14px 22px;
    border-radius: 14px;
    font-weight: 700;
    margin-top: 24px;
  }

  .ghost-btn {
    margin-top: 16px;
    font-weight: 700;
    color: #0f172a;
  }

  .danger-btn {
    margin-top: 40px;
    color: #b91c1c;
    font-weight: 700;
  }

  /* Info box */
  .info-box {
    background: #f8fafc;
    border-radius: 16px;
    padding: 16px;
    margin-bottom: 16px;
  }

  .info-box.muted {
    color: #94a3b8;
  }

  /* Mobile */
  @media (max-width: 900px) {
    .settings-layout {
      grid-template-columns: 1fr;
    }
  }

  /* ===============================
   GLOBAL BASE
   =============================== */

  html,
  body {
    background: #f8fafc;
    color: #0f172a;
    font-family: 'Instrument Sans', system-ui, -apple-system, sans-serif;
  }

  /* Neutral app background */
  .bg-neutral-bg {
    background: #f8fafc;
  }

  /* Display font helper */
  .font-display {
    font-family: 'Instrument Sans', system-ui, sans-serif;
  }

  /* ===============================
   PREMIUM UI TOKENS
   =============================== */

  /* Card shadow used everywhere */
  .premium-shadow {
    box-shadow:
      0 4px 30px -10px rgba(0, 0, 0, 0.04),
      0 2px 10px -5px rgba(0, 0, 0, 0.02);
  }

  /* Glass effect (for blobs / overlays) */
  .glass-ui {
    background: rgba(255, 255, 255, 0.7);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.5);
  }

  /* Abstract background blobs */
  .abstract-blob {
    filter: blur(60px);
    opacity: 0.55;
    mix-blend-mode: multiply;
  }

  /* ===============================
   NAV / HEADER
   =============================== */

  .nav-item-active {
    position: relative;
    color: #0f172a;
    font-weight: 800;
  }

  .nav-item-active::after {
    content: "";
    position: absolute;
    left: 0;
    bottom: -6px;
    width: 100%;
    height: 2px;
    background: #0f172a;
    border-radius: 999px;
  }

  /* Sticky header safety */
  header {
    position: sticky;
    top: 0;
    z-index: 50;
  }

  /* ===============================
   FORMS & INPUTS
   =============================== */

  input,
  textarea {
    outline: none;
    border: none;
    background: #f8fafc;
    color: #0f172a;
  }

  input:focus,
  textarea:focus {
    box-shadow: 0 0 0 3px rgba(15, 23, 42, 0.05);
  }

  /* Toggle switch (checkbox polish) */
  input[type="checkbox"] {
    width: 40px;
    height: 22px;
    appearance: none;
    background: #e5e7eb;
    border-radius: 999px;
    position: relative;
    cursor: pointer;
    transition: background 0.2s ease;
  }

  input[type="checkbox"]::after {
    content: "";
    width: 18px;
    height: 18px;
    background: white;
    border-radius: 50%;
    position: absolute;
    top: 2px;
    left: 2px;
    transition: transform 0.2s ease;
  }

  input[type="checkbox"]:checked {
    background: #0f172a;
  }

  input[type="checkbox"]:checked::after {
    transform: translateX(18px);
  }

  /* ===============================
   BUTTONS
   =============================== */

  button {
    cursor: pointer;
    border: none;
    background: none;
  }

  button:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  /* ===============================
   STATUS PILLS
   =============================== */

  .status {
    font-size: 11px;
    padding: 4px 10px;
    border-radius: 999px;
    font-weight: 700;
  }

  .status.verified {
    background: #ecfdf5;
    color: #047857;
  }

  .status.partial {
    background: #fffbeb;
    color: #92400e;
  }

  /* ===============================
   TABLE SAFETY
   =============================== */

  table {
    border-collapse: collapse;
  }

  th,
  td {
    vertical-align: middle;
  }

  /* ===============================
   MOBILE SAFETY
   =============================== */

  @media (max-width: 768px) {

    .px-12 {
      padding-left: 16px !important;
      padding-right: 16px !important;
    }

    .rounded-3xl,
    .rounded-\[2\.5rem\] {
      border-radius: 20px !important;
    }

    h1 {
      font-size: 28px !important;
    }

    h2 {
      font-size: 22px !important;
    }
  }

  /* Ultra small devices */
  @media (max-width: 420px) {
    h1 {
      font-size: 24px !important;
    }
  }

  .section-desc {
    color: #94a3b8;
    font-size: 14px;
    margin-bottom: 32px;
  }

  .integration-group {
    margin-bottom: 40px;
  }

  .integration-group h4 {
    font-size: 13px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.15em;
    color: #64748b;
    margin-bottom: 16px;
  }

  .integration-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 18px 0;
    border-bottom: 1px solid #f1f5f9;
  }

  .integration-item:last-child {
    border-bottom: none;
  }

  .integration-item .left {
    display: flex;
    gap: 14px;
  }

  .integration-item .icon {
    font-size: 22px;
  }

  .integration-item strong {
    font-size: 15px;
  }

  .integration-item p {
    font-size: 13px;
    color: #94a3b8;
  }

  .connect-btn {
    font-size: 12px;
    font-weight: 800;
    padding: 8px 14px;
    border-radius: 999px;
    background: #0f172a;
    color: white;
  }

  .connect-btn.connected {
    background: #ecfdf5;
    color: #047857;
  }

  .integration-item.disabled {
    opacity: 0.5;
  }

  .badge {
    font-size: 11px;
    font-weight: 800;
    padding: 4px 10px;
    border-radius: 999px;
    background: #f1f5f9;
    color: #475569;
  }

  .card {
    background: #ffffff;
    border-radius: 14px;
    padding: 24px;
    border: 1px solid #e6e8eb;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.04);
  }

  /* ===============================
   SECURITY / CHANGE PASSWORD
   =============================== */

  .security-card h3 {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .security-card h3 i {
    font-size: 22px;
  }

  .security-card h3 .bx {
    font-size: 22px;
  }

  /* Reuse existing field, add polish */
  .security-card .field {
    margin-bottom: 18px;
  }

  .security-card input {
    width: 100%;
    padding: 14px;
    border-radius: 14px;
    background: #f8fafc;
    font-weight: 500;
  }

  /* Actions */
  .action-row {
    display: flex;
    gap: 12px;
    margin-top: 28px;
  }

  /* Feedback states */
  .form-error {
    margin-top: 10px;
    font-size: 13px;
    font-weight: 600;
    color: #b91c1c;
  }

  .form-success {
    margin-top: 10px;
    font-size: 13px;
    font-weight: 700;
    color: #047857;
  }

  /* Mobile */
  @media (max-width: 640px) {
    .action-row {
      flex-direction: column;
    }

    .action-row button {
      width: 100%;
      justify-content: center;
    }
  }

  .primary-btn,
  .ghost-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    /* space between icon & text */
    line-height: 1;
    /* removes vertical text drift */
  }

  /* Material Symbols optical fix */
  .primary-btn .bx,
  .ghost-btn .bx {
    font-size: 18px;
    line-height: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    vertical-align: middle;
  }

  /* ===============================
   MOBILE RESPONSIVE PATCH ONLY
   =============================== */
  @media (max-width: 640px) {

    /* Page padding */
    .settings-page {
      padding: 24px 16px 80px;
    }

    /* Header */
    .settings-header h1 {
      font-size: 28px;
      line-height: 1.2;
    }

    .settings-header p {
      font-size: 14px;
    }

    /* Layout: stack */
    .settings-layout {
      gap: 24px;
      margin-top: 32px;
    }

    /* Tabs → horizontal scroll */
    .settings-tabs {
      display: flex;
      gap: 8px;
      padding: 8px;
      overflow-x: auto;
      border-radius: 16px;
    }

    .settings-tabs::-webkit-scrollbar {
      display: none;
    }

    .tab-btn {
      flex-shrink: 0;
      white-space: nowrap;
      padding: 10px 14px;
      font-size: 13px;
      border-radius: 999px;
    }

    /* Cards */
    .card {
      padding: 20px;
      border-radius: 20px;
    }

    .card h3 {
      font-size: 20px;
      margin-bottom: 20px;
    }

    /* Inline rows → vertical */
    .field.inline,
    .info-row,
    .toggle {
      flex-direction: column;
      align-items: flex-start;
      gap: 6px;
    }

    /* Status pills spacing */
    .status {
      align-self: flex-start;
    }

    /* Inputs */
    .field input {
      padding: 12px;
      border-radius: 12px;
    }

    /* Buttons */
    .primary-btn,
    .ghost-btn,
    .danger-btn {
      width: 100%;
      text-align: center;
    }

    .danger-btn {
      margin-top: 24px;
    }

    /* Integration items → stacked */
    .integration-item {
      flex-direction: column;
      align-items: flex-start;
      gap: 12px;
    }

    .integration-item .left {
      gap: 10px;
    }

    .integration-item strong {
      font-size: 14px;
    }

    .integration-item p {
      font-size: 12px;
    }

    .connect-btn,
    .badge {
      align-self: flex-start;
    }

    /* Security actions already columned, add spacing */
    .action-row {
      gap: 10px;
    }
  }


  .modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(15, 23, 42, 0.35);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 999;
  }

  .modal-card {
    width: 420px;
    max-width: 95%;
    background: white;
    padding: 32px;
    border-radius: 24px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.12);
  }

  .dialog-btn {
    padding: 10px 18px;
    border-radius: 10px;
    font-size: 11px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.14em;
  }

  .dialog-btn.primary {
    background: #0f172a;
    color: white;
  }

  .dialog-btn.danger {
    background: #ef4444;
    color: white;
  }

  .dialog-btn.muted {
    background: #f1f5f9;
    color: #64748b;
  }
  /* ============================= */
/* PROFILE MODAL ENHANCEMENTS */
/* ============================= */

.modal-card h3 {
  font-size: 22px;
  font-weight: 800;
  margin-bottom: 18px;
  color: #0f172a;
}

/* Field spacing */
.modal-card .field {
  margin-bottom: 18px;
}

/* Label */
.modal-card .field label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #94a3b8;
  font-weight: 700;
}

/* Input */
.modal-card input {
  width: 100%;
  margin-top: 6px;
  padding: 12px 14px;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  background: #f8fafc;
  font-weight: 600;
  font-size: 14px;
  transition: all 0.15s ease;
}

/* Input focus */
.modal-card input:focus {
  border-color: #0f172a;
  background: white;
  box-shadow: 0 0 0 3px rgba(15,23,42,0.08);
}

/* Modal buttons */
.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
}

/* Cancel button */
.ghost-btn {
  background: #f1f5f9;
  padding: 10px 16px;
  border-radius: 12px;
  font-weight: 700;
  border: none;
  transition: background 0.15s ease;
}

.ghost-btn:hover {
  background: #e2e8f0;
}

/* Save button */
.primary-btn {
  background: #0f172a;
  color: white;
  padding: 10px 18px;
  border-radius: 12px;
  font-weight: 800;
  border: none;
  transition: opacity 0.15s ease;
}

.primary-btn:hover {
  opacity: 0.9;
}

/* Mobile modal */
@media (max-width: 640px) {

  .modal-card {
    width: 90%;
    padding: 20px;
    border-radius: 16px;
  }

  .modal-card h3 {
    font-size: 20px;
  }

}
/* ============================= */
/* EDIT NAME BUTTON */
/* ============================= */

.link {
  font-size: 13px;
  font-weight: 800;
  color: #0f172a;
  background: none;
  border: none;
  padding: 6px 8px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;
}

/* Hover effect */
.link:hover {
  background: #f1f5f9;
  color: #000;
}

/* Active click */
.link:active {
  transform: scale(0.96);
}

/* Optional spacing when multiple buttons */
.toggle-row .link + .link {
  margin-left: 8px;
}
.danger-primary {
  background: #dc2626;
  color: white;
  padding: 12px 18px;
  border-radius: 12px;
  font-weight: 800;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: all 0.15s ease;
}

.danger-primary:hover {
  background: #b91c1c;
}
textarea {
  width: 100%;
  margin-top: 6px;
  padding: 14px;
  border-radius: 14px;
  background: #f8fafc;
  resize: none;
  min-height: 100px;
}
/* Select same as input */
select {
  width: 100%;
  margin-top: 6px;
  padding: 14px;
  border-radius: 14px;
  background: #f8fafc;
  border: none;
  outline: none;
  font-weight: 500;
  appearance: none; /* removes default arrow */
}

/* Custom arrow */
.field select {
  background-image: url("data:image/svg+xml,%3Csvg fill='%230f172a' viewBox='0 0 20 20'%3E%3Cpath d='M5 7l5 5 5-5H5z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 16px;
  padding-right: 40px;
}
.terms-box {
  display: flex;
  gap: 10px;
  margin-top: 16px;
  font-size: 13px;
  color: #64748b;
}

.terms-box input {
  margin-top: 3px;
}
</style>